import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, ButtonBase, Stack } from '@mui/material';
import { ChevronUp } from 'lucide-react';
import { ReactNode, useRef } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../translation/TranslationContext';
import useScrollAnchor from '../../hooks/useScrollAnchor';
import { useCollapsibleSectionStyles } from './collapsibleSection.styles';

interface CollapsibleSectionProps {
  title: LangEnum;
  isOpen: boolean;
  isHidden?: boolean;
  scrollElement?: HTMLElement | null;
  testId?: string;
  actions?: ReactNode;
  openHeaderContent?: ReactNode;
  children: ReactNode;
  onToggle: () => void;
}

const CollapsibleSection = ({
  title,
  isOpen,
  isHidden = false,
  scrollElement,
  testId,
  actions,
  openHeaderContent,
  children,
  onToggle,
}: CollapsibleSectionProps) => {
  const { classes, cx } = useCollapsibleSectionStyles({ isOpen });
  const { translate } = useTranslation();
  const headerRef = useRef<HTMLDivElement | null>(null);
  const { captureAnchor } = useScrollAnchor({
    anchorRef: headerRef,
    scrollElement,
  });

  const translatedTitle = translate(title);
  const hasOpenHeaderContent = isOpen && !!openHeaderContent;
  const isHeaderToggle = !hasOpenHeaderContent && !actions;

  const handleToggle = () => {
    captureAnchor();
    onToggle();
  };

  if (isHidden) {
    return <Box sx={{ display: 'none' }}>{children}</Box>;
  }

  const header = isHeaderToggle ? (
    <ButtonBase
      className={cx(classes.header, classes.headerButton, classes.headerInteractive)}
      aria-expanded={isOpen}
      onClick={handleToggle}
    >
      <Typography variant="overline" color="text.primary">
        {translatedTitle}
      </Typography>

      <Box className={classes.chevron}>
        <Icon icon={ChevronUp} size="md" />
      </Box>
    </ButtonBase>
  ) : (
    <Box className={cx(classes.header, classes.headerBox)}>
      {hasOpenHeaderContent ? (
        openHeaderContent
      ) : (
        <ButtonBase aria-expanded={isOpen} onClick={handleToggle}>
          <Typography variant="overline" color="text.primary">
            {translatedTitle}
          </Typography>
        </ButtonBase>
      )}

      <Box className={classes.headerEnd}>
        {actions}

        <ButtonBase
          className={classes.chevron}
          aria-label={translatedTitle}
          aria-expanded={isOpen}
          onClick={handleToggle}
        >
          <Icon icon={ChevronUp} size="md" />
        </ButtonBase>
      </Box>
    </Box>
  );

  return (
    <Stack ref={headerRef} className={classes.root} test-id={testId}>
      {header}

      {isOpen && (
        <Box paddingX={2} paddingBottom={2}>
          {children}
        </Box>
      )}
    </Stack>
  );
};

export default CollapsibleSection;
